import { forwardRef } from "react";
import { useTranslation } from "react-i18next";

import { faChevronDown } from "@fortawesome/pro-regular-svg-icons";

import { Icon } from "@/components";
import { DeepResearchResponseSection } from "@/pages/Private/redux/deepResearch/schema";
import { classNames } from "@/utils/classNames";

import {
	GenericSectionBody,
	InsightsBody,
	SectionBodyProps,
	SummaryBody,
	TalkingPointsBody,
} from "./ResearchSectionBody";
import { SourcePills } from "./SourcePills";
import { ResearchSectionKind, resolveTalkingPoints } from "./researchSections.utils";

const BODY_BY_KIND = {
	summary: SummaryBody,
	points: TalkingPointsBody,
	insights: InsightsBody,
	generic: GenericSectionBody,
} as const;

interface ResearchSectionCardProps extends Omit<SectionBodyProps, "section"> {
	section: DeepResearchResponseSection;
	kind: ResearchSectionKind;
	title: string;
	position: number;
	isCollapsed: boolean;
	onToggleCollapse: () => void;
}

/** How many items the section header advertises next to its title. */
const resolveItemCount = (
	kind: ResearchSectionKind,
	section: DeepResearchResponseSection
): number | null => {
	if (kind === "points") {
		return resolveTalkingPoints(section).length || null;
	}

	return section.subsections?.length || null;
};

export const ResearchSectionCard = forwardRef<HTMLDivElement, ResearchSectionCardProps>(
	(
		{ kind, title, position, isCollapsed, onToggleCollapse, section, sectionKey, ...bodyProps },
		ref
	) => {
		const { t } = useTranslation();
		const Body = BODY_BY_KIND[kind];
		const itemCount = resolveItemCount(kind, section);

		return (
			<div
				ref={ref}
				className="rounded-xl border border-border-light bg-white"
				data-testid={`research-section-${sectionKey}`}
			>
				<button
					aria-expanded={!isCollapsed}
					className="flex w-full cursor-pointer items-center gap-2.5 px-4 py-3 text-left"
					data-testid={`research-section-toggle-${sectionKey}`}
					type="button"
					onClick={onToggleCollapse}
				>
					<span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-md bg-light-blue text-[11px] font-bold text-brand-light">
						{position}
					</span>
					<span className="flex-1 text-sm font-semibold text-text">
						{title}
						{itemCount !== null && (
							<span className="ml-1.5 text-xs font-normal text-text-tertiary">({itemCount})</span>
						)}
					</span>
					<span className="flex items-center gap-1.5 text-xs font-medium text-text-meta">
						{isCollapsed
							? t("profileTracker.deepResearch.expand")
							: t("profileTracker.deepResearch.collapse")}
						<Icon
							className={classNames("h-3 w-3 transition-transform", isCollapsed && "-rotate-90")}
							icon={faChevronDown}
						/>
					</span>
				</button>

				{!isCollapsed && (
					<div className="px-4 pb-3">
						<Body section={section} sectionKey={sectionKey} {...bodyProps} />

						{/* Section-level citations only render where the body does not
						    already show them per fact. */}
						{kind === "insights" && !!section.sources?.length && (
							<div className="mt-2 flex flex-wrap items-center gap-1.5">
								<SourcePills
									portalContainer={bodyProps.portalContainer}
									sources={section.sources}
								/>
							</div>
						)}
					</div>
				)}
			</div>
		);
	}
);

ResearchSectionCard.displayName = "ResearchSectionCard";
